import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, Loader2, Image as ImageIcon, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { uploadService } from '@/lib/api/upload.service';

interface ImageUploadProps {
  /** Current image URL (can be external url or local upload url) */
  value?: string;
  /** Called when the URL changes (after upload or manual URL input) */
  onChange: (url: string | undefined) => void;
  /** Placeholder text for the URL input */
  placeholder?: string;
  /** Help text below the component */
  helpText?: string;
  /** Max file size in MB (display only — actual limit is server-side) */
  maxSizeMB?: number;
  /** Accepted file types */
  accept?: string;
  /** Additional class name for the root container */
  className?: string;
}

export function ImageUpload({
  value,
  onChange,
  placeholder,
  helpText,
  maxSizeMB = 10,
  accept = 'image/jpeg,image/png,image/webp,image/gif,image/svg+xml',
  className = '',
}: ImageUploadProps) {
  const { t } = useTranslation('common');
  const resolvedPlaceholder = placeholder ?? t('imageUpload.placeholder');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = useCallback(
    async (file: File) => {
      setError(null);

      // Client-side size check
      if (file.size > maxSizeMB * 1024 * 1024) {
        setError(t('imageUpload.errors.fileTooLarge', { maxSizeMB }));
        return;
      }

      setUploading(true);
      try {
        const result = await uploadService.uploadImage(file);
        onChange(result.url);
      } catch (err: unknown) {
        const msg =
          err instanceof Error
            ? err.message
            : (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            t('imageUpload.errors.uploadFailed');
        setError(msg);
      } finally {
        setUploading(false);
      }
    },
    [maxSizeMB, onChange]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void handleUpload(file);
      // Reset so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [handleUpload]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith('image/')) {
        void handleUpload(file);
      } else if (file) {
        setError(t('imageUpload.errors.dropImageOnly'));
      }
    },
    [handleUpload]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleRemove = useCallback(async () => {
    if (value) {
      // Try to delete from server if it's a local upload
      const filename = uploadService.extractFilename(value);
      if (filename) {
        try {
          await uploadService.deleteImage(filename);
        } catch {
          // Non-critical: file may already be deleted
        }
      }
    }
    onChange(undefined);
    setError(null);
  }, [value, onChange]);

  const hasPreview = value && (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/uploads/'));

  return (
    <div className={`space-y-2 ${className}`}>
      {/* URL Input row */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Link2 className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder={resolvedPlaceholder}
            value={value || ''}
            onChange={(e) => {
              setError(null);
              onChange(e.target.value || undefined);
            }}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="default"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          title={t('imageUpload.uploadTitle')}
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          <span className="ml-1.5 hidden sm:inline">{uploading ? t('imageUpload.uploading') : t('actions.upload')}</span>
        </Button>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleFileInputChange}
        aria-label={t('imageUpload.fileInputAria')}
        title={t('imageUpload.fileInputAria')}
      />

      {/* Drop zone (shown when no preview) */}
      {!hasPreview && !uploading && (
        <div
          className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors cursor-pointer
            ${dragActive
              ? 'border-primary bg-primary/5'
              : 'border-muted-foreground/25 hover:border-muted-foreground/50'
            }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <ImageIcon className="h-8 w-8 text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">
            {t('imageUpload.dropzoneText')}
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            {t('imageUpload.formatsAndSize', { maxSizeMB })}
          </p>
        </div>
      )}

      {/* Image preview */}
      {hasPreview && (
        <div className="relative overflow-hidden rounded-lg border bg-muted group">
          <img
            src={value}
            alt={t('imageUpload.previewAlt')}
            className="h-40 w-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => void handleRemove()}
            title={t('imageUpload.removeTitle')}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* Error message */}
      {error && <p className="text-xs text-destructive">{error}</p>}

      {/* Help text */}
      {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
    </div>
  );
}
